import React from 'react';
import { Box, Typography, Grid, Button, Chip, Avatar, Stack, IconButton, Tooltip } from '@mui/material';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import ReceiptLongRoundedIcon from '@mui/icons-material/ReceiptLongRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import SwapHorizRoundedIcon from '@mui/icons-material/SwapHorizRounded';
import { PRIMARY, PRIMARY_LIGHT } from '../../theme';
import { card, ghostBtn, dangerBtn, th, td } from '../uiKit';
import { inr, fmtDate, initialsFromName } from '../../utils/format';

const linkSx = { border: 'none', bgcolor: 'transparent', p: 0, cursor: 'pointer', fontFamily: 'inherit', fontSize: 'inherit', fontWeight: 700, color: PRIMARY, textAlign: 'left', '&:hover': { textDecoration: 'underline' } };

const Section = ({ title, children, sx }) => (
    <Box sx={{ ...card, overflow: 'hidden', height: '100%', ...sx }}>
        <Box sx={{ ...th, px: 2.5, py: 1.4 }}>{title}</Box>
        <Box sx={{ p: 2.5 }}>{children}</Box>
    </Box>
);

const Fact = ({ label, children }) => (
    <Box>
        <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: '#98A0AE', letterSpacing: 0.5, textTransform: 'uppercase' }}>{label}</Typography>
        <Typography component="div" sx={{ fontSize: 14, fontWeight: 600, color: '#0F172A', mt: 0.4 }}>{children || '—'}</Typography>
    </Box>
);

/**
 * Profile-style page for a single expense entry: a header with the amount and status, the full
 * set of fields, who it involved (with their running balance), and other entries in the category.
 */
export default function ExpenseProfile({ config, entry, rows, onOpenEntry, onOpenEmployee, onToggleStatus, onDelete, onBack }) {
    const { noun, extraFields = [], trackStatus = false, shootTypes = null } = config;
    const title = entry.notes?.trim() ? entry.notes.trim().split('\n')[0] : entry.category;
    const paid = entry.status === 'Paid';
    const related = rows.filter((r) => r.category === entry.category && r.id !== entry.id).sort((a, b) => b.date.localeCompare(a.date)).slice(0, 5);

    return (
        <Box sx={{ px: { xs: 1.5, md: 2.5 }, pt: 2.5, pb: 3 }}>
            <Box sx={{ ...card, p: 2.5, mb: 2.5 }}>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2.5} sx={{ alignItems: { md: 'center' } }}>
                    {onBack && (
                        <Tooltip arrow title="Back">
                            <IconButton onClick={onBack} aria-label="Back" sx={{ alignSelf: { md: 'center' }, border: '1px solid #E6EAF1', borderRadius: '8px', color: '#475569', '&:hover': { bgcolor: '#F1EEFE', color: PRIMARY } }}>
                                <ArrowBackRoundedIcon />
                            </IconButton>
                        </Tooltip>
                    )}
                    <Avatar sx={{ width: 64, height: 64, bgcolor: PRIMARY_LIGHT, color: PRIMARY, borderRadius: '14px' }}>
                        <ReceiptLongRoundedIcon sx={{ fontSize: 32 }} />
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontSize: 20, fontWeight: 800, color: '#0F172A' }} noWrap>{title}</Typography>
                        <Typography sx={{ fontSize: 13, color: '#64748B', mt: 0.3 }}>
                            {noun} expense · {entry.category} · {fmtDate(entry.date)}
                        </Typography>
                        <Stack direction="row" spacing={1} sx={{ mt: 1.2, flexWrap: 'wrap', rowGap: 1 }}>
                            {shootTypes && entry.shootType && <Chip size="small" label={entry.shootType} sx={{ fontWeight: 700 }} />}
                            {trackStatus && (
                                <Chip size="small" label={entry.status} sx={{ fontWeight: 700, bgcolor: paid ? '#E8F7EF' : '#FDEBEE', color: paid ? '#2F8F5F' : '#C2475A' }} />
                            )}
                        </Stack>
                    </Box>
                    <Box sx={{ textAlign: { md: 'right' } }}>
                        <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: '#98A0AE', letterSpacing: 0.5 }}>{trackStatus ? 'PRICE' : 'AMOUNT'}</Typography>
                        <Typography sx={{ fontSize: 30, fontWeight: 800, color: '#0F172A', lineHeight: 1.1 }}>{inr(entry.amount)}</Typography>
                        <Stack direction="row" spacing={1} sx={{ mt: 1.5, justifyContent: { md: 'flex-end' } }}>
                            {trackStatus && (
                                <Button size="small" startIcon={<SwapHorizRoundedIcon />} onClick={() => onToggleStatus(entry)} sx={{ ...ghostBtn, px: 1.5 }}>
                                    Mark {paid ? 'Unpaid' : 'Paid'}
                                </Button>
                            )}
                            <Button size="small" startIcon={<DeleteOutlineRoundedIcon />} onClick={() => onDelete(entry.id)} sx={{ ...dangerBtn, px: 1.5 }}>Delete</Button>
                        </Stack>
                    </Box>
                </Stack>
            </Box>

            <Grid container spacing={2.5}>
                <Grid size={{ xs: 12, md: 8 }}>
                    <Section title="EXPENSE DETAILS">
                        <Grid container spacing={2.5}>
                            <Grid size={{ xs: 6, sm: 4 }}><Fact label="Date">{fmtDate(entry.date)}</Fact></Grid>
                            <Grid size={{ xs: 6, sm: 4 }}><Fact label="Category">{entry.category}</Fact></Grid>
                            {shootTypes && <Grid size={{ xs: 6, sm: 4 }}><Fact label="Shoot type">{entry.shootType}</Fact></Grid>}
                            {extraFields.map((f) => <Grid key={f.key} size={{ xs: 6, sm: 4 }}><Fact label={f.label}>{entry[f.key]}</Fact></Grid>)}
                            <Grid size={{ xs: 6, sm: 4 }}><Fact label="Payment mode">{entry.paymentMode}</Fact></Grid>
                            {trackStatus && <Grid size={{ xs: 6, sm: 4 }}><Fact label="Status">{entry.status}</Fact></Grid>}
                            <Grid size={12}><Fact label={trackStatus ? 'Description' : 'Notes'}>{entry.notes}</Fact></Grid>
                        </Grid>
                    </Section>
                </Grid>

                <Grid size={{ xs: 12, md: 4 }}>
                    {config.employees ? (
                        <Section title="EMPLOYEES">
                            {(entry.empStats || []).length ? (
                                <Stack spacing={1.4}>
                                    {entry.empStats.map((e) => (
                                        <Stack key={e.code || e.name} direction="row" spacing={1.2} sx={{ alignItems: 'center' }}>
                                            <Avatar sx={{ width: 32, height: 32, fontSize: 12, fontWeight: 700, bgcolor: PRIMARY_LIGHT, color: PRIMARY }}>{initialsFromName(e.name)}</Avatar>
                                            <Box sx={{ minWidth: 0 }}>
                                                <Box component="button" onClick={() => onOpenEmployee(e.code)} sx={{ ...linkSx, fontSize: 14 }}>{e.name || e.code}</Box>
                                                <Typography sx={{ fontSize: 11, color: '#64748B' }}>
                                                    Net balance {e.netBalance < 0 ? '−' : ''}{inr(Math.abs(e.netBalance))}
                                                </Typography>
                                            </Box>
                                        </Stack>
                                    ))}
                                </Stack>
                            ) : <Typography sx={{ fontSize: 13, color: '#98A0AE' }}>No employees tagged.</Typography>}
                        </Section>
                    ) : (
                        <Section title="PAYMENT">
                            <Stack spacing={2}>
                                <Fact label="Amount">{inr(entry.amount)}</Fact>
                                <Fact label="Paid via">{entry.paymentMode}</Fact>
                                {trackStatus && <Fact label="Status">{entry.status}</Fact>}
                            </Stack>
                        </Section>
                    )}
                </Grid>

                {related.length > 0 && (
                    <Grid size={12}>
                        <Section title={`MORE IN ${entry.category.toUpperCase()}`} sx={{ height: 'auto' }}>
                            <Box sx={{ mx: -2.5, mb: -2.5, mt: -2.5 }}>
                                {related.map((r) => (
                                    <Box
                                        key={r.id} onClick={() => onOpenEntry(r.id)}
                                        sx={{ ...td, display: 'grid', gridTemplateColumns: '120px 1fr 110px', px: 2.5, cursor: 'pointer', '&:hover': { bgcolor: '#FAFAFE' } }}
                                    >
                                        <Typography sx={{ fontSize: 13 }}>{fmtDate(r.date)}</Typography>
                                        <Typography sx={{ fontSize: 13, color: '#64748B' }} noWrap>{r.notes || '—'}</Typography>
                                        <Typography sx={{ fontSize: 13, fontWeight: 800, textAlign: 'right' }}>{inr(r.amount)}</Typography>
                                    </Box>
                                ))}
                            </Box>
                        </Section>
                    </Grid>
                )}
            </Grid>
        </Box>
    );
}
